<template>
  <sub-section
    title="Could buying some guaranteed income help?"
    :showHelp="false"
  >
    <template v-if="showOfferToCheck">
      <div class="row">
        <div class="col">
          <p>
            Some tests below show shortfalls. Using some of your non protected
            pots to buy some guaranteed income may remove these without having
            to take less or pay more. Would you like to try this?
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col text-center">
          <button class="btn btn-success mx-3" @click="onLetsSeeClicked" aria-label="Let's see if buying some guaranteed income could help resolve some of the shortfalls in the test scenarios.">
            Let's see
          </button>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="row">
        <div class="col">
          <p>
            {{ subscriptionText }}
            <help
              title="Guaranteed income"
              :text="HelpText.guaranteedIncome(annuityType)"
            />
          </p>
        </div>
      </div>
    </template>
  </sub-section>

  <modal
    ref="guaranteedIncomeModal"
    title="Test with guaranteed income"
    idKey="allScenarioGuaranteedIncomeModal"
    :close-button-aria-label="modalCloseButtonAriaLabel"
  >
    <template v-if="isEvaluating">
      <div class="row">
        <div class="col">
          <p>Evaluating...</p>
        </div>
      </div>
      <div class="row">
        <div class="col text-center">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="row">
        <div class="col">
          <table class="table table-bordered">
            <tr
              v-for="activeScenario in activeScenarios"
              v-bind:key="activeScenario.scenarioType"
            >
              <td>
                Scenario {{ activeScenario.number }} -
                {{ getScenarioName(activeScenario.scenarioType) }}
              </td>
              <td>
                <p class="text-center p-0 m-0">
                  <i
                    class="bi fs-1"
                    :class="exclaimationOrCheck(activeScenario.outcome)"
                  ></i>
                </p>
              </td>
            </tr>
          </table>
        </div>
      </div>
      <div v-if="!isEvaluating" class="row">
        <div class="col">
          <p>{{ outcomeMessage }}</p>
          <template v-if="isViable">
            <p>Do you want to include this in your plan?</p>
          </template>
        </div>
      </div>
      <div v-if="hasError" class="row alert alert-info mx-1 px-0">
        <div class="col">
          <p>{{ errorMessage }}</p>
        </div>
      </div>
    </template>
    <div class="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
      <button
        type="button"
        v-if="showYesButton"
        :disabled="!yesBtnEnabled"
        class="btn px-5 btn-secondary"
        aria-label="Yes"
        @click="onYesClicked"
      >
        Yes
        <span
          v-if="implementingSolution"
          class="spinner-border spinner-border-sm mb-1"
          role="status"
          aria-hidden="true"
        ></span>
      </button>
      <button
        type="button"
        class="btn px-5 btn-secondary"
        aria-label="No"
        @click="onCloseClicked"
      >
        {{ noButtonText }}
      </button>
    </div>
  </modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import { Scenario } from "@/guiide/ts/scenario";
import ScenarioType from "@/guiide/ts/scenarioType";
import ScenarioOutcome from "@/guiide/ts/scenarioOutcome";
import ScenarioInfo from "@/guiide/ts/scenarioInfo";
import Modal from "@/guiide/components/common/Modal.vue";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import { app } from "@/guiide/ts/app";
import { formatter } from "@/shared/ts/formatter";
import AnnuityType from "@/guiide/ts/annuityType";

export default defineComponent({
  name: "AllScenariosGuaranteedIncome",
  emits: ["subscribed-to-offer"],
  components: {
    SubSection,
    Modal,
    Help
  },
  props: {
    solveEnabled: { type: Boolean, required: false, default: false }
  },
  data() {
    return {
      lowerGrowth: {
        scenarioType: ScenarioType.LowerGrowth,
        number: 1,
        outcome: ScenarioOutcome.Unknown
      },
      liveLonger: {
        scenarioType: ScenarioType.LiveThreeYearsLonger,
        number: 2,
        outcome: ScenarioOutcome.Unknown
      },
      marketCrash: {
        scenarioType: ScenarioType.PotsFallTwentyPercent,
        number: 3,
        outcome: ScenarioOutcome.Unknown
      },
      needExtraMoney: {
        scenarioType: ScenarioType.NeedExtraMoneyForLastTwoYears,
        number: 4,
        outcome: ScenarioOutcome.Unknown
      },
      isEvaluating: true,
      isViable: false,
      implementingSolution: false,
      yesBtnEnabled: true,
      errorMessage: "",
      ScenarioType,
      HelpText
    };
  },
  setup() {
    const solverModal = ref(null);
    const guaranteedIncomeModal = ref(null);
    return {
      solverModal,
      guaranteedIncomeModal
    };
  },
  computed: {
    activeScenarios: function() {
      return [
        this.lowerGrowth,
        this.liveLonger,
        this.marketCrash,
        this.needExtraMoney
      ];
    },
    showOfferToCheck: function() {
      if (this.$store.state.retiree == null) {
        return true;
      }
      return !this.$store.state.retiree.guaranteedIncome.subscribed;
    },
    showYesButton: function() {
      return !this.isEvaluating && this.isViable;
    },
    noButtonText: function() {
      if (this.isEvaluating) {
        return "Close";
      } else {
        return this.isViable ? "No" : "Close";
      }
    },
    outcomeMessage: function(): string {
      if (this.isEvaluating) {
        return "";
      }
      const allocationPercentage = this.$store.state.retiree.guaranteedIncome
        .propositionAllocation;
      if (this.isViable) {
        const annualIncome = formatter.formatCurrency(
          this.$store.state.retiree.guaranteedIncome.propositionAnnualIncome
        );
        return `If you can exchange ${allocationPercentage}% of your non protected pension pots for some guaranteed income (${annualIncome} each year from retirement, based on illustrative rates) you would see an improved outcome in some of these tests. You can find out more on this in our Dashboard, Track and Help page.`;
      } else {
        return `Exchanging ${allocationPercentage}% of your non protected pension pots for some guaranteed income from retirement did not improve the result of any of the tests. You can model other amounts and dates to buy some guaranteed income from in our Dashboard, Track and Help page.`;
      }
    },
    hasError: function(): boolean {
      return this.errorMessage.length > 0;
    },
    subscriptionText: function(): string {
      if (this.$store.state.retiree == null) {
        return "";
      }
      if (!this.$store.state.retiree.guaranteedIncome.subscribed) {
        return "An illustrative guaranteed income has been included in your plan.";
      } else {
        const annualIncome = formatter.formatCurrency(
          this.$store.state.retiree.guaranteedIncome.annualIncome
        );
        return `An illustrative guaranteed income, paying ${annualIncome} each year, has been included in your plan.`;
      }
    },
    annuityType: function(): AnnuityType {
      return this.$store.state.configuration.annuityType;
    },
    modalCloseButtonAriaLabel: function(): string {
      if (this.isEvaluating) {
        return "Close button for the guaranteed income shortfall popup which is currently evaluating - please wait.";
      } else {
        const postfix = this.isViable
          ? "Do you want to include this in your plan? Tab to Yes and No buttons, or press space or enter to close the popup."
          : "Press space or enter to close the popup."
        return `The evaluation has completed. ${this.outcomeMessage} ${postfix}`;
      }
    }
  },
  methods: {
    async onLetsSeeClicked() {
      this.initialise();
      this.guaranteedIncomeModal.showModal();
      const scenarios = await app.runAllTestScenariosForGuaranteedIncome();
      for (const scenario of scenarios) {
        this.updateOutcome(scenario.scenarioType, scenario.outcome);
      }
      this.isEvaluating = false;
      this.isViable = this.isOfferViable();
    },
    initialise() {
      this.isEvaluating = true;
      this.isViable = false;
      this.implementingSolution = false;
      this.updateOutcome(ScenarioType.LowerGrowth, ScenarioOutcome.Unknown);
      this.updateOutcome(
        ScenarioType.LiveThreeYearsLonger,
        ScenarioOutcome.Unknown
      );
      this.updateOutcome(
        ScenarioType.PotsFallTwentyPercent,
        ScenarioOutcome.Unknown
      );
      this.updateOutcome(
        ScenarioType.NeedExtraMoneyForLastTwoYears,
        ScenarioOutcome.Unknown
      );
    },
    isOfferViable(): boolean {
      for (const guaranteedIncomeScenario of this.activeScenarios) {
        if (
          this.outcomeImprovedWithGuaranteedIncome(guaranteedIncomeScenario)
        ) {
          return true;
        }
      }
      return false;
    },
    outcomeImprovedWithGuaranteedIncome(guaranteedIncomeScenario: Scenario) {
      const outcomeWithoutGI = this.getOutcomeWithoutGuaranteedIncome(
        guaranteedIncomeScenario.scenarioType
      );
      return (
        outcomeWithoutGI == ScenarioOutcome.Fail &&
        guaranteedIncomeScenario.outcome == ScenarioOutcome.Pass
      );
    },
    getOutcomeWithoutGuaranteedIncome(
      scenarioType: ScenarioType
    ): ScenarioOutcome {
      let scenario: Scenario;
      switch (scenarioType) {
        case ScenarioType.LiveThreeYearsLonger:
          scenario = this.$store.state.youLiveLongerScenario;
          break;
        case ScenarioType.PotsFallTwentyPercent:
          scenario = this.$store.state.marketCrashScenario;
          break;
        case ScenarioType.NeedExtraMoneyForLastTwoYears:
          scenario = this.$store.state.needSomeCareScenario;
          break;
        case ScenarioType.LowerGrowth:
          scenario = this.$store.state.lowerGrowthScenario;
          break;
      }
      return scenario.outcome;
    },
    async onYesClicked() {
      this.implementingSolution = true;
      this.yesBtnEnabled = false;
      this.errorMessage = "";

      const responseTracker = await app.applyGuaranteedIncome(
        null,
        null,
        "GITest"
      );

      if (responseTracker.isOk) {
        this.$emit("subscribed-to-offer");
        this.guaranteedIncomeModal.closeModal();
        return;
      }

      this.implementingSolution = false;
      this.yesBtnEnabled = true;

      if (responseTracker.trackerWillHandleFailure) {
        responseTracker.handleFailure();
      } else {
        this.errorMessage = responseTracker.message;
      }
    },
    onCloseClicked() {
      this.guaranteedIncomeModal.closeModal();
    },
    onSolveTakeLess() {
      this.solverModal.showModal();
      this.scenarioSolver.checkCouldSolveByTakeLess();
    },
    getScenarioName(scenarioType: ScenarioType) {
      return ScenarioInfo.getName(scenarioType);
    },
    exclaimationOrCheck(scenarioOutcome: ScenarioOutcome): string {
      switch (scenarioOutcome) {
        case ScenarioOutcome.Unknown:
          return "bi-question-circle-fill text-secondary";
        case ScenarioOutcome.Pass:
          return "bi-check-circle-fill text-success";
        default:
          return "bi-exclamation-circle-fill text-danger";
      }
    },
    testingClicked() {
      this.needExtraMoney.outcome = ScenarioOutcome.Fail;
    },
    updateOutcome(
      scenarioType: ScenarioType,
      scenarioOutcome: ScenarioOutcome
    ) {
      switch (scenarioType) {
        case ScenarioType.LowerGrowth:
          this.lowerGrowth.outcome = scenarioOutcome;
          return;
        case ScenarioType.LiveThreeYearsLonger:
          this.liveLonger.outcome = scenarioOutcome;
          return;
        case ScenarioType.PotsFallTwentyPercent:
          this.marketCrash.outcome = scenarioOutcome;
          return;
        case ScenarioType.NeedExtraMoneyForLastTwoYears:
          this.needExtraMoney.outcome = scenarioOutcome;
          return;
        default:
          return;
      }
    }
  }
});
</script>
